'use client';

import {ShowAllButton} from 'iq-questions/components/show_all_button';
import {TopicBarChart} from 'iq-questions/components/topic_bar_chart';
import type {TopicWeight} from 'iq-questions/types';
import {useRef, useState} from 'react';

interface Props {
  topicWeights: Array<TopicWeight>;
  // Optional controls rendered between the subtitle and the chart (e.g. domain filters).
  filterSlot?: React.ReactNode;
  // Canonical topic order for stable per-topic colors when the chart is filtered.
  colorOrder?: Array<string>;
}

const TOP_N = 8;

function buildVisibleTopics(sorted: Array<TopicWeight>, showAll: boolean): Array<TopicWeight> {
  if (showAll || sorted.length <= TOP_N) return sorted;
  const othersWeight = sorted.slice(TOP_N).reduce((sum, t) => sum + t.weight, 0);
  return [
    ...sorted.slice(0, TOP_N),
    {topic: 'Others', weight: othersWeight},
  ];
}

export function TopicDistributionCard({topicWeights, filterSlot, colorOrder}: Props) {
  const [showAll, setShowAll] = useState(false);
  const chartRef = useRef<HTMLDivElement>(null);

  if (!topicWeights?.length) return null;

  const sorted = [...topicWeights].sort((a, b) => b.weight - a.weight);
  const hasMore = sorted.length > TOP_N;
  const visible = buildVisibleTopics(sorted, showAll);

  return (
    <div className='bg-white border border-iq-grey-light rounded-xl p-6 mb-8'>
      <h3 className='text-lg font-bold text-iq-black mb-1'>What they test you on</h3>
      <p className='text-base text-iq-grey-medium mb-4'>Topics based on recent interview experiences.</p>
      {filterSlot}
      <div ref={chartRef}>
        <TopicBarChart visible={visible} showAll={showAll} colorOrder={colorOrder} />
      </div>
      {hasMore && (
        <ShowAllButton
          total={sorted.length}
          showAll={showAll}
          onToggle={() => {
            setShowAll((v) => !v);
            chartRef.current?.scrollIntoView({behavior: 'smooth', block: 'start'});
          }}
        />
      )}
    </div>
  );
}
